import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import PageBanner from "@/components/PageBanner";
import Reveal from "@/components/Reveal";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAILS = [
  { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
  { icon: Phone, label: "Phone", value: SITE.phone, href: `tel:${SITE.phoneHref}` },
  { icon: MapPin, label: "Address", value: SITE.address, href: null },
  { icon: Clock, label: "Hours", value: "Mon–Fri, 9am – 5pm EST", href: null },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We've received your message and will be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-ink/40 focus:border-sage focus:ring-2 focus:ring-sage/30";

  return (
    <div data-testid="contact-page">
      <PageBanner
        eyebrow="Contact Us"
        title="We'd love to hear from you"
        subtitle="Questions about our products, orders, or wholesale? Send us a note and our team will get back to you shortly."
        testId="contact-banner"
      />

      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Details */}
          <Reveal>
            <h2 className="font-heading text-3xl text-forest md:text-4xl">Get in touch</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-ink/65">
              Reach out through any of the channels below, or use the form and we'll reply by email.
            </p>
            <div className="mt-10 space-y-6">
              {DETAILS.map((d) => {
                const inner = (
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sand">
                      <d.icon className="h-5 w-5 text-sage" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{d.label}</p>
                      <p className="mt-1 text-base text-forest">{d.value}</p>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.label} href={d.href} data-testid={`contact-${d.label.toLowerCase()}`} className="block transition-opacity hover:opacity-70">
                    {inner}
                  </a>
                ) : (
                  <div key={d.label} data-testid={`contact-${d.label.toLowerCase()}`}>{inner}</div>
                );
              })}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-line bg-sand p-7 md:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-forest">Name</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-forest">Email</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-forest">Subject</label>
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="How can we help?"
                  className={inputCls}
                />
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-forest">Message</label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Write your message..."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest disabled:opacity-60 sm:w-auto"
              >
                {loading ? "Sending..." : "Send Message"}
                {!loading && <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
